import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban } from "lucide-react";

import { DataTable } from "components/ui/DataTable";
import { CloseLabTestDialog } from "features/labOrders/components/CloseLabTestDialog/CloseLabTestDialog";
import { ROWS_OPTIONS } from "features/labOrders/constants";
import { useOutstandingLabTests } from "features/labOrders/hooks/useOutstandingLabTests";
import {
  filterWorklistRows,
  getOrderingGpOptions,
  getSectionOptions,
  toWorklistRows,
} from "features/labOrders/utils/outstandingLabTests";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { pathGenerators } from "utils/routes";

import { getOutstandingLabTestsColumns } from "./outstandingLabTestsColumns";
import s from "./OutstandingLabTestsTable.module.css";

const FLAGS_GROUP = "flags";
const GP_GROUP = "orderingStaffId";
const SECTION_GROUP = "section";

const OVERDUE_FLAG = "overdue";
const MINE_FLAG = "mine";

const EMPTY_FILTERS = {
  [FLAGS_GROUP]: [],
  [GP_GROUP]: null,
  [SECTION_GROUP]: null,
};

/**
 * The practice's outstanding eLab tests (KIN-2087): one row per test still waiting for
 * its result, oldest first, with the overdue ones flagged.
 *
 * Who should see this list is still open with Darren — ordering GP, every GP, or
 * reception too — so the ticket's own instruction is followed: it is built for every
 * practice user, with a "Mine only" filter for the GP who wants their own.
 *
 * Filtering is done here rather than by the endpoint. The response is the whole waiting
 * list in one go, and the GP and section menus are built from it; filtering server-side
 * would shrink the list the menus come from, so choosing one GP would remove every other
 * GP from the menu and there would be no way back.
 */
export const OutstandingLabTestsTable = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [closingTest, setClosingTest] = useState(null);

  const { data, isLoading, isError } = useOutstandingLabTests();
  const { data: staffInfo } = useStaffInfo();
  const currentStaffId = staffInfo?.staffMember?.id ?? null;

  const rows = useMemo(() => toWorklistRows(data ?? []), [data]);

  // Memoised: it feeds the filtering memo below, and a fresh `[]` fallback per render
  // would re-run it on every render.
  const flags = useMemo(() => filters[FLAGS_GROUP] ?? [], [filters]);
  const isMineOnly = flags.includes(MINE_FLAG);

  // "Mine only" wins over a GP picked by name: it is the more specific statement of the
  // two, and leaving both in force would silently return nothing whenever they disagree.
  const orderingStaffId = isMineOnly ? currentStaffId : filters[GP_GROUP];

  const visibleRows = useMemo(
    () =>
      filterWorklistRows(rows, {
        overdueOnly: flags.includes(OVERDUE_FLAG),
        orderingStaffId,
        section: filters[SECTION_GROUP],
      }),
    [rows, flags, orderingStaffId, filters]
  );

  const filtersConfig = useMemo(
    () => [
      {
        id: FLAGS_GROUP,
        label: "Show",
        type: "multi",
        options: [
          { value: OVERDUE_FLAG, label: "Overdue only" },
          // Offered only to a staff member we can identify; without an id it would
          // filter to nothing and look like an empty worklist.
          ...(currentStaffId ? [{ value: MINE_FLAG, label: "Mine only" }] : []),
        ],
      },
      {
        id: GP_GROUP,
        label: "Ordering GP",
        type: "single",
        options: getOrderingGpOptions(rows),
      },
      {
        id: SECTION_GROUP,
        label: "Lab section",
        type: "single",
        options: getSectionOptions(rows),
      },
    ],
    [rows, currentStaffId]
  );

  const columns = useMemo(() => getOutstandingLabTestsColumns(), []);

  const rowActions = useMemo(
    () => [
      {
        label: "Won't come back",
        icon: Ban,
        onClick: (test) => setClosingTest(test),
      },
    ],
    []
  );

  const isFiltered =
    flags.length > 0 || !!filters[GP_GROUP] || !!filters[SECTION_GROUP];

  return (
    <div className={s.wrapper} data-testid="outstanding-lab-tests-table">
      <DataTable
        columns={columns}
        data={visibleRows}
        getRowId={(test) => test.id}
        isLoading={isLoading}
        isError={isError}
        noScroll
        // The row opens the patient's lab orders. There is no deep link to a single
        // order yet, so it lands on the list the order is on rather than nowhere.
        onRowClick={(test) => navigate(pathGenerators.patient.labOrders(test.patientId))}
        rowActions={rowActions}
        actionsColumnWidth="40px"
        emptyTitle={
          isError ? "Error loading outstanding tests" : "Nothing is outstanding"
        }
        emptyText={
          isError
            ? "Unable to load the outstanding lab tests. Please try again later."
            : isFiltered
            ? "No outstanding test matches these filters."
            : "Every test ordered through eLab has come back."
        }
        filtersConfig={filtersConfig}
        filters={{
          value: filters,
          onChange: (groupId, value) =>
            setFilters((previous) => ({ ...previous, [groupId]: value })),
        }}
        rowsValue={rowsPerPage}
        onRowsChange={setRowsPerPage}
        rowsOptions={ROWS_OPTIONS}
      />

      <CloseLabTestDialog
        test={closingTest}
        onClose={() => setClosingTest(null)}
      />
    </div>
  );
};
